import { FC, memo, useEffect, useState } from 'react'

import { useFocusNodeById, useSetFocus } from '@please/lrud'
import { useNavigate } from 'react-router-dom'

import {
  purchaseWithCredits,
  updateCreditWallets,
} from '@/actions/creditWallets'
import { Button } from '@/components/Button/Button'
import { updateUserRightsOfEvents } from '@/components/PPVPopup/actions/updateUserRightsOfEvents'
import {
  PurchaseStatus,
  AvailablePPVWallet,
} from '@/components/PPVPopup/PPVPopup'
import { Direction } from '@/constants/focus'
import { paths } from '@/constants/paths'
import { WatchButton } from '@/containers/EventDetailPage/Buttons'
import { useEventVideoSources } from '@/hooks/useEventVideoSources'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { usePPVPopupStore } from '@/store/usePPVPopupStore'

import { Actions } from './styles'

const PPVActionsRaw: FC<{
  isPayablePPVWallet: boolean
  activePPVWallet?: AvailablePPVWallet
  purchaseItemId: string
  isFutureChannelEvent: boolean
  isLiveNow: boolean
  isWarningScreen: boolean
  setIsWarningScreen: (value: boolean) => void
}> = ({
  isPayablePPVWallet,
  activePPVWallet,
  purchaseItemId,
  isFutureChannelEvent,
  isLiveNow,
  isWarningScreen,
  setIsWarningScreen,
}) => {
  const focusId = 'PPV_popup_actions'
  const setFocus = useSetFocus()
  const { t } = useCommonTranslation()
  const navigate = useNavigate()

  const { ppvPopup, setPpvPopup, purchaseStatus, setPurchaseStatus } =
    usePPVPopupStore((state) => state)
  const isPurchaseProcess = purchaseStatus === PurchaseStatus.PROCESS
  const isPurchaseSuccess = purchaseStatus === PurchaseStatus.SUCCESS
  const isPurchaseFailed = purchaseStatus === PurchaseStatus.FAILED
  const isPurchaseCompleted = isPurchaseSuccess || isPurchaseFailed

  const [defaultFocusChild, setDefaultFocusChild] = useState(1)

  const onMove = (e: MoveEvent) => {
    const currentChildIndex =
      e.direction === Direction.Forward
        ? Number(e.prevChildIndex) + 1
        : Number(e.prevChildIndex) - 1
    setDefaultFocusChild(currentChildIndex)
  }
  const focusNode = useFocusNodeById(focusId)

  const showWatchButton =
    isPurchaseSuccess && !isFutureChannelEvent && !!ppvPopup
  const showRedeemButton = !isPurchaseCompleted && isPayablePPVWallet
  const showGetMoreCreditsButton = !showRedeemButton && !showWatchButton

  const { eventVideoSources } = useEventVideoSources(
    showWatchButton ? ppvPopup.event : null,
  )

  useEffect(() => {
    const childrenLength = focusNode?.children.length || 0
    const hasOneButton =
      !showRedeemButton &&
      (!showWatchButton || !eventVideoSources) &&
      !!childrenLength
    const hasTwoButtons = childrenLength > 1

    if (hasOneButton || (hasTwoButtons && !!purchaseStatus)) {
      setFocus(focusId)
    }
  }, [focusNode?.children])

  const redeemCreditHandler = async () => {
    if (purchaseStatus !== null) {
      return
    }

    const purchaseData = {
      purchaseItems: [
        {
          type: 'Event',
          id: purchaseItemId,
          walletId: activePPVWallet?.walletId,
          quantity: 1,
        },
      ],
    }
    try {
      setPurchaseStatus(PurchaseStatus.PROCESS)
      await purchaseWithCredits(purchaseData)
      await updateUserRightsOfEvents(purchaseItemId)
      await updateCreditWallets()
      setPurchaseStatus(PurchaseStatus.SUCCESS)
    } catch (e) {
      setPurchaseStatus(PurchaseStatus.FAILED)
    } finally {
      setIsWarningScreen(false)
    }
  }

  const warningHandler = () => {
    setIsWarningScreen(true)
  }

  const closeHandler = () => {
    if (isPurchaseProcess) {
      return
    }
    ppvPopup && setFocus(ppvPopup.prevFocusId)
    setPpvPopup(null)
  }

  const goToSubscriptions = () => {
    closeHandler()
    navigate(`${paths.SETTINGS}/${paths.SETTINGS_SUBSCRIPTIONS}`)
  }

  const labelCloseButton =
    isPurchaseSuccess && !isFutureChannelEvent
      ? t('ppvPopup.watchLater')
      : isWarningScreen
      ? t('cancel')
      : t('close')

  return (
    <Actions
      focusId={focusId}
      defaultFocusChild={defaultFocusChild}
      onMove={onMove}
      $disabled={isPurchaseProcess}
      disabled={isPurchaseProcess}
    >
      <Button text={labelCloseButton} onClick={closeHandler} />
      {showRedeemButton && (
        <Button
          text={t('ppvPopup.redeemCredit')}
          onClick={
            !isWarningScreen && isLiveNow ? warningHandler : redeemCreditHandler
          }
        />
      )}
      {showWatchButton && eventVideoSources && (
        <WatchButton
          text={t('ppvPopup.toEvent')}
          event={ppvPopup.event}
          isPPVPopup
          eventVideoSources={eventVideoSources}
        />
      )}
      {showGetMoreCreditsButton && (
        <Button
          text={t('ppvPopup.buttonGetMoreCredits')}
          onClick={goToSubscriptions}
        />
      )}
    </Actions>
  )
}

export const PPVActions = memo(PPVActionsRaw)
